// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/CaseDetail/components/MatchResultsSection.tsx

import type {
  CommunityNavigatorNextAction,
  NavigatorCaseMatchResult,
  NavigatorCaseMatchRun,
  NavigatorCaseWorkflow,
} from "../types";
import {
  formatDateTime,
  formatMatchScore,
  getMatchResultsViewMode,
  isExcludedMatch,
  isUsableMatchResult,
  toTextList,
} from "../intakeUtils";
import TextList from "./TextList";

export const MAX_PRIMARY_MATCHES_TO_DISPLAY = 3;
const MATCH_RESULTS_SECTION_ID = "navigator-match-results";

type MatchResultsSectionProps = {
  latestMatchRun: NavigatorCaseMatchRun | null | undefined;
  matchResults: NavigatorCaseMatchResult[];
  workflow: NavigatorCaseWorkflow | null;
  nextAction?: CommunityNavigatorNextAction | null;
  matchRunError: string | null;
  matchRunSuccess: string | null;
  forwardError?: string | null;
  forwardSuccess?: string | null;
  isForwardingMatch?: boolean;
  forwardingMatchResultId?: number | null;
  showConsentRequiredWarning?: boolean;
  onUpdateConsent?: () => void;
  onForwardMatch?: (
    result: NavigatorCaseMatchResult
  ) => void | Promise<void>;
  onViewProviderProfile?: (familyDoctorId: number) => void;
  onChooseAnotherProvider?: () => void;
};

function getEligibilityClass(status: string | null | undefined): string {
  const normalized = String(status || "").trim().toLowerCase();

  if (normalized === "eligible") return "is-eligible";
  if (normalized === "caution") return "is-caution";
  if (normalized === "noteligible" || normalized === "excluded") {
    return "is-not-eligible";
  }

  return "is-unknown";
}

function getEligibilityLabel(status: string | null | undefined): string {
  const normalized = String(status || "").trim();

  if (normalized === "NotEligible") return "Not eligible";
  if (!normalized) return "Unknown";

  return normalized;
}

function getEligibilitySortRank(status: string | null | undefined): number {
  const normalized = String(status || "").trim().toLowerCase();

  if (normalized === "eligible") return 0;
  if (normalized === "caution") return 1;
  return 2;
}

function getDisplayRank(
  result: NavigatorCaseMatchResult,
  fallbackIndex: number
): number {
  const rank = Number(result.rankNumber || 0);
  return Number.isFinite(rank) && rank > 0 ? rank : fallbackIndex + 1;
}

export function sortMatchResults(
  matchResults: NavigatorCaseMatchResult[]
): NavigatorCaseMatchResult[] {
  return matchResults
    .map((result, originalIndex) => ({ result, originalIndex }))
    .sort((a, b) => {
      const aRank = Number(a.result.rankNumber || 0);
      const bRank = Number(b.result.rankNumber || 0);

      const aHasRank = Number.isFinite(aRank) && aRank > 0;
      const bHasRank = Number.isFinite(bRank) && bRank > 0;

      if (aHasRank && bHasRank && aRank !== bRank) {
        return aRank - bRank;
      }

      if (aHasRank && !bHasRank) return -1;
      if (!aHasRank && bHasRank) return 1;

      const eligibilityDiff =
        getEligibilitySortRank(a.result.eligibilityStatus) -
        getEligibilitySortRank(b.result.eligibilityStatus);

      if (eligibilityDiff !== 0) return eligibilityDiff;

      const scoreDiff =
        Number(b.result.matchScore || 0) - Number(a.result.matchScore || 0);

      if (scoreDiff !== 0) return scoreDiff;

      return a.originalIndex - b.originalIndex;
    })
    .map((item) => item.result);
}

/** Primary recommendation cards: first N usable results only (CN-R16). */
export function getPrimaryUsableMatchResults(
  matchResults: NavigatorCaseMatchResult[],
  maxPrimary: number = MAX_PRIMARY_MATCHES_TO_DISPLAY
): NavigatorCaseMatchResult[] {
  return sortMatchResults(matchResults)
    .filter(isUsableMatchResult)
    .slice(0, maxPrimary);
}

function getDisabledForwardReason({
  workflow,
  isExcluded,
  hasForwardHandler,
  alreadyForwarded,
  canForwardThisProvider,
  providerResponseLabel,
}: {
  workflow: NavigatorCaseWorkflow | null;
  isExcluded: boolean;
  hasForwardHandler: boolean;
  alreadyForwarded: boolean;
  canForwardThisProvider: boolean;
  providerResponseLabel: string | null;
}) {
  if (alreadyForwarded || !canForwardThisProvider) {
    return providerResponseLabel
      ? `Already forwarded: ${providerResponseLabel}`
      : "This provider has already received this case.";
  }

  if (isExcluded) return "This match cannot be forwarded.";

  if (!workflow?.canForwardToProvider) {
    return "This case is not ready to forward yet.";
  }

  if (!hasForwardHandler) return "Forwarding endpoint not connected yet.";

  return null;
}

export default function MatchResultsSection({
  latestMatchRun,
  matchResults,
  workflow,
  nextAction = null,
  matchRunError,
  matchRunSuccess,
  forwardError = null,
  forwardSuccess = null,
  isForwardingMatch = false,
  forwardingMatchResultId = null,
  showConsentRequiredWarning = false,
  onUpdateConsent,
  onForwardMatch,
  onViewProviderProfile,
  onChooseAnotherProvider,
}: MatchResultsSectionProps) {
  const primaryMatchResults = getPrimaryUsableMatchResults(matchResults);
  const hasLatestMatchRun = Boolean(latestMatchRun);
  const viewMode = getMatchResultsViewMode({
    nextActionCode: nextAction?.code,
    hasLatestMatchRun,
    usablePrimaryCount: primaryMatchResults.length,
  });

  const showRecommendations = viewMode === "showRecommendations";
  const hasPrimaryResults =
    showRecommendations && primaryMatchResults.length > 0;
  const hasForwardHandler = typeof onForwardMatch === "function";
  const hasProviderProfileHandler = typeof onViewProviderProfile === "function";
  const hasChooseAnotherProviderHandler =
    typeof onChooseAnotherProvider === "function";
  const canChooseAnotherProvider =
    Boolean(workflow?.canForwardToProvider) && hasChooseAnotherProviderHandler;

  function renderMatchResultCard(
    result: NavigatorCaseMatchResult,
    index: number
  ) {
    const displayRank = getDisplayRank(result, index);
    const matchReasons = toTextList(result.matchReasons);
    const matchCautions = toTextList(result.matchCautions);
    const isExcluded = isExcludedMatch(result);
    const providerForwardState = result as NavigatorCaseMatchResult & {
      alreadyForwarded?: boolean;
      latestProviderRequestStatus?: string | null;
      providerResponseLabel?: string | null;
      canForwardThisProvider?: boolean;
    };
    const alreadyForwarded = Boolean(providerForwardState.alreadyForwarded);
    const providerResponseLabel =
      providerForwardState.providerResponseLabel ||
      (alreadyForwarded ? "Forwarded to clinic" : "Not forwarded yet");
    const canForwardThisProvider =
      providerForwardState.canForwardThisProvider !== false &&
      !alreadyForwarded;
    const isForwardingThisMatch =
      isForwardingMatch &&
      forwardingMatchResultId === result.navigatorCaseMatchResultId;
    const disabledForwardReason = getDisabledForwardReason({
      workflow,
      isExcluded,
      hasForwardHandler,
      alreadyForwarded,
      canForwardThisProvider,
      providerResponseLabel,
    });
    const canForwardThisResult = !disabledForwardReason && !isForwardingMatch;
    const eligibilityClass = getEligibilityClass(result.eligibilityStatus);
    const forwardHintId = `cn-match-forward-hint-${result.navigatorCaseMatchResultId}`;

    return (
      <article
        className={[
          "cn-match-result-card",
          eligibilityClass,
          displayRank === 1 ? "is-top-match" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        key={result.navigatorCaseMatchResultId}
      >
        <div className="cn-match-result-header">
          <div className="cn-match-result-rank">
            <span>#{displayRank}</span>
            {displayRank === 1 ? <em>Top match</em> : null}
          </div>

          <div className="cn-match-result-provider">
            <h3>{result.familyDoctorName || "Unnamed provider"}</h3>
            <p>{result.clinicName || "Clinic not specified"}</p>
          </div>

          <div className="cn-match-result-score">
            <strong>{formatMatchScore(result.matchScore)}</strong>
            <span>{getEligibilityLabel(result.eligibilityStatus)}</span>
          </div>
        </div>

        <div className="cn-match-result-provider-response">
          <span>Provider response</span>
          <strong>{providerResponseLabel}</strong>
        </div>

        <div className="cn-match-result-content">
          <div className="cn-match-result-panel cn-match-result-reasons">
            <h4>Why this matched</h4>
            <TextList
              items={matchReasons}
              emptyText="No match reasons recorded."
            />
          </div>

          <div className="cn-match-result-panel cn-match-result-cautions">
            <h4>Review before forwarding</h4>
            <TextList
              items={matchCautions}
              emptyText="No cautions recorded."
            />
          </div>
        </div>

        <div className="cn-match-result-actions">
          {hasProviderProfileHandler ? (
            <button
              type="button"
              className="cn-match-result-secondary-action"
              onClick={() => onViewProviderProfile?.(result.familyDoctorId)}
            >
              View provider profile
            </button>
          ) : null}

          <button
            type="button"
            disabled={!canForwardThisResult}
            onClick={() => onForwardMatch?.(result)}
            aria-describedby={
              !canForwardThisResult &&
              !isForwardingThisMatch &&
              disabledForwardReason
                ? forwardHintId
                : undefined
            }
          >
            {isForwardingThisMatch
              ? "Forwarding..."
              : alreadyForwarded
              ? "Already forwarded"
              : "Select and forward"}
          </button>

          {!canForwardThisResult &&
          !isForwardingThisMatch &&
          disabledForwardReason ? (
            <span id={forwardHintId}>{disabledForwardReason}</span>
          ) : null}
        </div>
      </article>
    );
  }

  function renderStatusPanel() {
    if (viewMode === "neverMatched") {
      return (
        <div className="cn-match-results-empty">
          <h3>Find matches</h3>
          <p>
            Run matching to generate provider recommendations for this case.
            Use <strong>Find matches</strong> in the navigator workspace when
            intake is ready.
          </p>
        </div>
      );
    }

    if (viewMode === "matchingNeedsUpdate") {
      return (
        <div className="cn-match-results-empty cn-match-results-stale">
          <h3>Matching needs to be updated</h3>
          <p>
            The intake information has changed. Run matching again to generate
            recommendations using the current intake. Previous match runs remain
            in history and are not shown as current recommendations.
          </p>
        </div>
      );
    }

    if (viewMode === "noSuitableMatch") {
      return (
        <div className="cn-match-results-empty cn-match-results-no-suitable">
          <h3>No suitable match found</h3>
          <p>
            The latest match run did not identify a provider that is currently
            eligible or suitable with caution based on the information recorded
            for this case. You can run matching again, choose another provider
            manually, edit intake, or close the case using the navigator
            workspace actions.
          </p>
        </div>
      );
    }

    return null;
  }

  return (
    <section
      className="cn-case-detail-card cn-case-detail-wide cn-match-results-section"
      id={MATCH_RESULTS_SECTION_ID}
    >
      <div className="cn-match-results-hero">
        <div>
          <p className="cn-match-results-eyebrow">Navigator decision support</p>
          {hasPrimaryResults ? (
            <>
              <h2>Top recommended providers</h2>
              <p>
                Review the top recommended matches that are currently eligible
                or suitable with caution. Matching is decision support only and
                does not guarantee attachment.
              </p>
            </>
          ) : viewMode === "noSuitableMatch" ? (
            <>
              <h2>No suitable match found</h2>
              <p>
                Matching did not identify a usable provider recommendation for
                this case based on the recorded intake and provider criteria.
              </p>
            </>
          ) : viewMode === "matchingNeedsUpdate" ? (
            <>
              <h2>Matching needs to be updated</h2>
              <p>
                Recommendations from a previous match run are no longer current
                for this case.
              </p>
            </>
          ) : (
            <>
              <h2>Provider matching</h2>
              <p>
                Run matching after intake is complete to generate provider
                recommendations for this case.
              </p>
            </>
          )}
        </div>

        <div className="cn-match-results-hero-actions">
          <div className="cn-match-results-hero-badge">
            {hasPrimaryResults
              ? `Top ${primaryMatchResults.length} recommended`
              : viewMode === "noSuitableMatch"
              ? "No suitable match"
              : viewMode === "matchingNeedsUpdate"
              ? "Update matching"
              : "No match yet"}
          </div>

          {hasChooseAnotherProviderHandler ? (
            <div className="cn-match-results-manual-forward">
              <button
                type="button"
                className="cn-match-results-manual-action"
                disabled={!canChooseAnotherProvider}
                onClick={onChooseAnotherProvider}
              >
                Choose another provider
              </button>

              {!workflow?.canForwardToProvider ? (
                <span>
                  This case is not ready to forward to a manually selected
                  provider yet.
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {matchRunError ? (
        <div className="cn-case-detail-form-error" role="alert">
          {matchRunError}
        </div>
      ) : null}

      {matchRunSuccess ? (
        <div className="cn-case-detail-form-success" role="status">
          {matchRunSuccess}
        </div>
      ) : null}

      {showConsentRequiredWarning ? (
        <div
          className="cn-case-detail-form-warning cn-match-consent-warning"
          role="status"
        >
          <span>
            Consent must be recorded before forwarding to a provider.
          </span>
          {typeof onUpdateConsent === "function" ? (
            <button
              type="button"
              className="cn-match-consent-update-action"
              onClick={onUpdateConsent}
            >
              Update consent
            </button>
          ) : null}
        </div>
      ) : null}

      {forwardError ? (
        <div className="cn-case-detail-form-error" role="alert">
          {forwardError}
        </div>
      ) : null}

      {forwardSuccess ? (
        <div className="cn-case-detail-form-success" role="status">
          {forwardSuccess}
        </div>
      ) : null}

      {hasPrimaryResults && latestMatchRun ? (
        <div className="cn-match-results-stats">
          <div>
            <span>Decision support</span>
            <strong>Match run completed</strong>
          </div>

          <div>
            <span>Run at</span>
            <strong>{formatDateTime(latestMatchRun.runAt)}</strong>
          </div>

          <div>
            <span>Displayed results</span>
            <strong>{primaryMatchResults.length}</strong>
          </div>

          <div>
            <span>Top score</span>
            <strong>{formatMatchScore(latestMatchRun.topScore)}</strong>
          </div>
        </div>
      ) : (
        renderStatusPanel()
      )}

      {hasPrimaryResults ? (
        <div className="cn-match-results-group">
          <div className="cn-match-results-group-header">
            <div>
              <h3>Top recommended matches</h3>
              <p>
                These are the highest-ranked eligible or caution-level options
                from the latest match run.
              </p>
            </div>

            <span>Top {primaryMatchResults.length}</span>
          </div>

          <div className="cn-match-results-list">
            {primaryMatchResults.map((result, index) =>
              renderMatchResultCard(result, index)
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
